import React, { useState, useEffect } from 'react';
import { FiCheck, FiShoppingCart, FiStar, FiAlertCircle } from 'react-icons/fi';

const ProductSelectionRenderer = ({ data, onVariantSelect, onAddToCart }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [quantities, setQuantities] = useState({});

  const { message, product, variants, variantGroups, instructions } = data;
  
  // Force multiple selection mode for variant groups
  const selectionType = variantGroups && variantGroups.length > 0 ? 'multiple' : (data.selectionType || 'multiple');
  

  const handleItemToggle = (itemId) => {
    if (selectionType === 'single') {
      setSelectedItems([itemId]);
    } else if (selectionType === 'multiple') {
      const isSelected = selectedItems.includes(itemId);
      if (isSelected) {
        setSelectedItems(selectedItems.filter(id => id !== itemId));
      } else {
        setSelectedItems([...selectedItems, itemId]);
      }
    }
  };

  const handleQuantityChange = (variantId, quantity) => {
    setQuantities(prev => ({
      ...prev,
      [variantId]: Math.max(0, quantity)
    }));
  };

  const handleAddToCart = () => {
    if (onAddToCart && selectedItems.length > 0) {
      onAddToCart(selectedItems);
    }
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'in_stock': return 'text-green-600';
      case 'limited': return 'text-yellow-600';
      case 'out_of_stock': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getAvailabilityIcon = (availability) => {
    switch (availability) {
      case 'in_stock': return <FiCheck className="w-4 h-4" />;
      case 'limited': return <FiAlertCircle className="w-4 h-4" />;
      case 'out_of_stock': return <FiAlertCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency || 'CAD'
    }).format(price);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{message}</h3>
        {instructions && (
          <p className="text-sm text-gray-600">{instructions}</p>
        )}
      </div>

      {/* Product Info */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-start space-x-4">
          {product.image && (
            <img 
              src={product.image} 
              alt={product.name}
              className="w-16 h-16 object-cover rounded-lg"
            />
          )}
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">{product.name}</h4>
            {product.description && (
              <p className="text-sm text-gray-600 mt-1">{product.description}</p>
            )}
            {product.basePrice && (
              <p className="text-sm font-medium text-gray-900 mt-1">
                Starting from {formatPrice(product.basePrice, product.currency)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Variants or Variant Groups */}
      <div className="space-y-6">
        {/* Simple Variants */}
        {variants && variants.length > 0 && (
          <div className="space-y-3">
            {variants.map((variant) => {
              const isSelected = selectedItems.includes(variant.variantId);
              const isDisabled = variant.availability === 'out_of_stock';
              const isRecommended = variant.isRecommended;

              return (
                <div
                  key={variant.variantId}
                  className={`border rounded-lg p-4 transition-all duration-200 ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : isDisabled 
                        ? 'border-gray-200 bg-gray-50 opacity-60'
                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                  }`}
                  onClick={() => !isDisabled && handleItemToggle(variant.variantId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Selection indicator */}
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected 
                          ? 'bg-blue-500 border-blue-500' 
                          : 'border-gray-300'
                      }`}>
                        {isSelected && <FiCheck className="w-3 h-3 text-white" />}
                      </div>

                      {/* Variant image */}
                      {variant.image && (
                        <img 
                          src={variant.image} 
                          alt={variant.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}

                      {/* Variant details */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h5 className="font-medium text-gray-900">{variant.name}</h5>
                          {isRecommended && (
                            <div className="flex items-center space-x-1 text-yellow-600">
                              <FiStar className="w-4 h-4 fill-current" />
                              <span className="text-xs font-medium">Recommended</span>
                            </div>
                          )}
                        </div>
                        
                        {variant.description && (
                          <p className="text-sm text-gray-600 mt-1">{variant.description}</p>
                        )}

                        {/* Attributes */}
                        {variant.attributes && Object.keys(variant.attributes).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {Object.entries(variant.attributes).map(([key, value]) => (
                              <span 
                                key={key}
                                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                              >
                                {key}: {value}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Availability */}
                        <div className={`flex items-center space-x-1 mt-2 ${getAvailabilityColor(variant.availability)}`}>
                          {getAvailabilityIcon(variant.availability)}
                          <span className="text-sm font-medium capitalize">
                            {variant.availability.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Price and quantity */}
                    <div className="text-right">
                      <div className="flex items-center space-x-2">
                        {variant.originalPrice && variant.originalPrice > variant.price && (
                          <span className="text-sm text-gray-500 line-through">
                            {formatPrice(variant.originalPrice, variant.currency)}
                          </span>
                        )}
                        <span className="font-semibold text-gray-900">
                          {formatPrice(variant.price, variant.currency)}
                        </span>
                      </div>

                      {/* Quantity selector for selected variants */}
                      {isSelected && selectionType === 'quantity' && (
                        <div className="flex items-center space-x-2 mt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(variant.variantId, (quantities[variant.variantId] || 1) - 1);
                            }}
                            className="w-6 h-6 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                          >
                            -
                          </button>
                          <span className="w-8 text-center text-sm">
                            {quantities[variant.variantId] || 1}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(variant.variantId, (quantities[variant.variantId] || 1) + 1);
                            }}
                            className="w-6 h-6 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Variant Groups */}
        {variantGroups && variantGroups.length > 0 && (
          <div className="space-y-6">
            {variantGroups.map((group) => (
              <div key={group.groupId} className="border border-gray-200 rounded-lg p-4">
                {/* Group Header */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-1">{group.message}</h4>
                  {group.description && (
                    <p className="text-sm text-gray-600">{group.description}</p>
                  )}
                </div>

                {/* Group Items */}
                <div className="grid gap-3">
                  {group.items.map((item) => {
                    const isSelected = selectedItems.includes(item.itemId);
                    const isDisabled = item.availability === 'out_of_stock';
                    const isRecommended = item.isRecommended;
                    

                    return (
                      <div
                        key={item.itemId}
                        className={`border rounded-lg p-3 transition-all duration-200 ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : isDisabled 
                              ? 'border-gray-200 bg-gray-50 opacity-60'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}
                        onClick={() => !isDisabled && handleItemToggle(item.itemId)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {/* Selection indicator */}
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                              isSelected 
                                ? 'bg-blue-500 border-blue-500' 
                                : 'border-gray-300'
                            }`}>
                              {isSelected && <FiCheck className="w-2 h-2 text-white" />}
                            </div>

                            {/* Item image */}
                            {item.image && (
                              <img 
                                src={item.image} 
                                alt={item.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            )}

                            {/* Item details */}
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h6 className="font-medium text-gray-900">{item.name}</h6>
                                {isRecommended && (
                                  <div className="flex items-center space-x-1 text-yellow-600">
                                    <FiStar className="w-3 h-3 fill-current" />
                                    <span className="text-xs font-medium">Recommended</span>
                                  </div>
                                )}
                              </div>
                              
                              {item.description && (
                                <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                              )}

                              {/* Attributes */}
                              {item.attributes && Object.keys(item.attributes).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {Object.entries(item.attributes).map(([key, value]) => (
                                    <span 
                                      key={key}
                                      className="px-1 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                                    >
                                      {key}: {value}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Availability */}
                              <div className={`flex items-center space-x-1 mt-1 ${getAvailabilityColor(item.availability)}`}>
                                {getAvailabilityIcon(item.availability)}
                                <span className="text-xs font-medium capitalize">
                                  {item.availability.replace('_', ' ')}
                                </span>
                              </div>
                              
                            </div>
                          </div>

                          {/* Price and quantity */}
                          <div className="text-right">
                            <div className="flex items-center space-x-2">
                              {item.originalPrice && item.originalPrice > item.price && (
                                <span className="text-xs text-gray-500 line-through">
                                  {formatPrice(item.originalPrice, item.currency)}
                                </span>
                              )}
                              <span className="font-semibold text-gray-900 text-sm">
                                {formatPrice(item.price, item.currency)}
                              </span>
                            </div>

                            {/* Quantity selector for selected items */}
                            {isSelected && selectionType === 'quantity' && (
                              <div className="flex items-center space-x-1 mt-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuantityChange(item.itemId, (quantities[item.itemId] || 1) - 1);
                                  }}
                                  className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-xs"
                                >
                                  -
                                </button>
                                <span className="w-6 text-center text-xs">
                                  {quantities[item.itemId] || 1}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuantityChange(item.itemId, (quantities[item.itemId] || 1) + 1);
                                  }}
                                  className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-xs"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons */}
      {selectedItems.length > 0 && (
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={() => {
              if (onVariantSelect) {
                onVariantSelect(selectedItems);
              }
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Confirm Selection
          </button>
          <button
            onClick={handleAddToCart}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <FiShoppingCart className="w-4 h-4" />
            <span>Add to Cart</span>
          </button>
        </div>
      )}

      {/* Selection summary */}
      {selectedItems.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductSelectionRenderer;
